// 封裝瀏覽器能力選路與每通電話的 Worker 資源，不讓通話生命週期依賴特定瀏覽器實作。
export const KEY_FRAME_REQUEST_METHOD = Object.freeze({
  // Safari／Firefox：把視訊接到 Worker，從 Worker 呼叫 generateKeyFrame()。
  ENCODED_TRANSFORM: 'encoded-transform',
  // Chrome：使用 Chromium 已實作的 W3C WebRTC Extensions encodingOptions.keyFrame。
  SET_PARAMETERS: 'set-parameters'
})

// 「本該走 encoded-transform 卻因故失敗」的回退原因，用來決定是否對外告警。
// 刻意不含以下三個正常結果，避免誤報：
// - encoded-transform-infrastructure-unsupported：瀏覽器根本沒有 Transform 基礎 API
// - transformer-generate-key-frame-unsupported：Chrome 的正常結果，本來就該走 setParameters
// - transform-disposed：通話在能力探測期間就結束了
const ABNORMAL_FALLBACK_REASONS = Object.freeze([
  'worker-ready-timeout',
  'transformer-capability-timeout',
  'worker-script-error',
  'worker-message-deserialization-failed',
  'encoded-transform-initialization-failed',
  'encoded-transform-attachment-failed',
  'path-selection-failed'
])

// Worker 腳本必須與頁面同源。正式環境的 publicPath 指向 CDN（跨網域），
// 直接沿用 publicPath 會讓 new Worker() 拋 SecurityError，因此改用同源絕對路徑。
// 規則與 src/js/utils/shared.js 建立 SharedWorker 時完全相同：
// 開發伺服器掛在網站根目錄，建置後的站台掛在 /mgmt 之下。
// 【同步注意】部署路徑若變更，shared.js 與本檔兩處都要一起修改。
const WORKER_PATH_PREFIX = process.env.NODE_ENV === 'development' ? '' : '/mgmt'
const KEY_FRAME_WORKER_URL = `${WORKER_PATH_PREFIX}/js/keyFrameWorker.js?env=${process.env.VUE_APP_ENV}&v=${process.env.VUE_APP_VERSION}`

// 最多等待 Worker 5 秒回報「是否支援 generateKeyFrame()」。
// 探測逾時會把整通通話永久釘死在 set-parameters，而該路徑在 iOS 正是修復前的黑屏成因，
// 因此寧可等久一點：正常情況下 Chrome 立即回報不支援、Safari 於 rtctransform 觸發時回報，
// 這個上限只有在真的異常時才會用到。
const KEY_FRAME_CAPABILITY_TIMEOUT_MS = 5000
// 發出 IDR 請求後，最多等待 Worker 5 秒回覆成功或失敗。
const KEY_FRAME_REQUEST_TIMEOUT_MS = 5000

// 每個 IDR 請求的流水號，用來把 Worker 回覆對應回正確的 Promise。
let keyFrameRequestSequence = 0

/**
 * Intent:
 * 將瀏覽器相容性差異封裝成單一 key frame 請求路徑，通話流程不需要辨識瀏覽器。
 *
 * 路徑：
 * 1. encoded-transform：Safari／Firefox 的 Worker 內提供 generateKeyFrame()。
 * 2. set-parameters：Chromium 已實作的 W3C WebRTC Extensions encodingOptions.keyFrame。
 *
 * Why:
 * 僅偵測 RTCRtpScriptTransform 不足以判斷 generateKeyFrame() 是否存在；Chrome 支援
 * Transform 管線，但目前未提供 Worker 端 generateKeyFrame()。因此先建立「未掛載」
 * 的 Transform 讓 Worker 回報實際能力；只有確認支援後才掛到 sender。如此 Chrome
 * 的即時視訊管線完全不會被能力探測改動，並固定使用原有的 setParameters 路徑。
 */
const hasEncodedTransformInfrastructure = (sender) => {
  // 這裡只確認建立 Transform 所需的基礎 API 是否存在，不改動 sender。
  // 支援 RTCRtpScriptTransform 的 Chrome 也會通過此檢查，所以仍須進入 Worker
  // 檢查 generateKeyFrame()；僅有 Transform 基礎 API 不代表該方法存在。
  return typeof RTCRtpScriptTransform === 'function' &&
    typeof Worker === 'function' &&
    'transform' in sender
}

// notifyWorkerFailure 由管理模組層傳入（見 createKeyFrameRequestManager），
// 因為本控制器是每個 sender 各建一份，無法自行持有管理模組層的告警回呼。
const createEncodedTransformControl = (sender, notifyWorkerFailure) => {
  // 建立 Dedicated Worker。腳本由 vue.config.js 的 keyFrameWorker entry 產出，
  // 以同源絕對路徑載入（見 KEY_FRAME_WORKER_URL 的說明）。
  // 本控制器固定為「一個 Worker 對應一個 transformer」，因此直接使用 Worker
  // 內建的 postMessage()/message 通道，不需要 MessageChannel 區分多個 transformer。
  const worker = new Worker(KEY_FRAME_WORKER_URL)

  // key 是 requestId，value 保存該次 request() 的 resolve、reject 與逾時計時器。
  const pendingRequests = new Map()
  // disposed 避免清理函式重複執行；attached 表示 Transform 是否已接入視訊 sender。
  let disposed = false
  let attached = false
  // workerReady 只記錄期限內是否收到 Worker ready，用來細分 timeout log；
  // 未收到 ready 本身不足以判定是腳本載入、排程或通訊哪一環失敗。
  let workerReady = false
  // capability 只能完成一次；Worker 回覆與逾時計時器可能先後抵達。
  let capabilitySettled = false
  let resolveCapability
  let transform

  // 呼叫端 await 這個 Promise，等待 Worker 回報 generateKeyFrame() 是否存在。
  const capability = new Promise((resolve) => {
    resolveCapability = resolve
  })

  const settleCapability = (supported, reason, error = null) => {
    // 只採用第一個結果，避免「Worker 已回覆」之後又被 timeout 覆蓋。
    if (capabilitySettled) return

    capabilitySettled = true
    clearTimeout(capabilityTimeoutId)
    resolveCapability({ supported, reason, error })
  }

  const capabilityTimeoutId = setTimeout(() => {
    // ready 已收到：Worker 全域通道正常，但期限內未收到 rtctransform 能力回報。
    // ready 未收到：期限內未收到 Worker ready；僅記錄觀察結果，不推論是載入或通訊失敗。
    settleCapability(
      false,
      workerReady ? 'transformer-capability-timeout' : 'worker-ready-timeout'
    )
  }, KEY_FRAME_CAPABILITY_TIMEOUT_MS)

  const handleMessage = ({ data }) => {
    // 第一類訊息：Worker 腳本完成載入，而且全域 postMessage 通道可用。
    if (data && data.type === 'key-frame-worker-ready') {
      workerReady = true
      return
    }

    // 第二類訊息：rtctransform 事件觸發後，回報是否看得到 generateKeyFrame()。
    if (data && data.type === 'key-frame-capability') {
      settleCapability(
        data.supported === true,
        data.supported === true
          ? 'transformer-generate-key-frame-supported'
          : 'transformer-generate-key-frame-unsupported'
      )
      return
    }

    // Worker 內 readable → writable 的透明轉送中斷。掛載 Transform 後這條轉送是 outbound
    // 視訊的必經單點，中斷後視訊永久停送且不自愈，與「選路階段就退回 setParameters」性質不同，
    // 因此走 onWorkerFailure 而非 onFallback，讓上層能分開告警。
    //
    // 與下方 handleWorkerError 同理，只有已掛載才回報：探測用的 Transform 也會執行 pipeTo，
    // 其中斷不影響任何視訊，回報只會讓 error 等級的告警混入正常情況。
    if (data && data.type === 'key-frame-pipe-failed') {
      if (!attached) return

      const pipeErrorInfo = data.error || {}
      const pipeError = new Error(pipeErrorInfo.message || 'Encoded Transform 轉送已中斷。')
      pipeError.name = pipeErrorInfo.name || 'Error'
      notifyWorkerFailure('transform-pipe-failed', pipeError)
      return
    }

    // 第三類訊息：先前某次 IDR 請求的成功／失敗結果。
    if (!data || (data.type !== 'key-frame-generated' && data.type !== 'key-frame-failed')) return

    const pendingRequest = pendingRequests.get(data.requestId)

    // 請求已逾時或控制器已清理時會從 Map 移除；後續抵達的 Worker 回覆直接忽略。
    if (!pendingRequest) return

    clearTimeout(pendingRequest.timeoutId)
    pendingRequests.delete(data.requestId)

    if (data.type === 'key-frame-generated') {
      pendingRequest.resolve()
      return
    }

    const errorInfo = data.error || {}
    const error = new Error(errorInfo.message || 'Encoded Transform 無法產生 key frame。')
    error.name = errorInfo.name || 'Error'
    pendingRequest.reject(error)
  }

  const handleWorkerError = (event) => {
    const error = event.error instanceof Error
      ? event.error
      : new Error(event.message || 'Key frame Worker 腳本執行失敗。')

    // 能力已判定後才發生的 Worker 錯誤會被 settleCapability 直接略過，選路結果不再改變；
    // 但此時 Transform 可能已掛載，Worker 故障等同 outbound 視訊中斷，不另行回報就完全無跡可循。
    //
    // 只有已掛載才回報：未掛載的探測用 Worker（Chrome 回報不支援、或逾時回退）故障不影響
    // 任何視訊，若一併回報會讓 error 等級的告警混入正常情況，淹沒真正的中斷事件。
    if (capabilitySettled) {
      if (attached) {
        notifyWorkerFailure('worker-error-after-capability-settled', error)
      }
      return
    }

    settleCapability(false, 'worker-script-error', error)
  }

  const handleWorkerMessageError = () => {
    settleCapability(
      false,
      'worker-message-deserialization-failed',
      new Error('主執行緒無法反序列化 Key frame Worker 訊息。')
    )
  }

  const removeWorkerListeners = () => {
    worker.removeEventListener('message', handleMessage)
    worker.removeEventListener('error', handleWorkerError)
    worker.removeEventListener('messageerror', handleWorkerMessageError)
  }

  worker.addEventListener('message', handleMessage)
  worker.addEventListener('error', handleWorkerError)
  worker.addEventListener('messageerror', handleWorkerMessageError)

  try {
    // 兩階段選路：建構 Transform 只啟動 Worker 能力探測，不先改動 sender。
    // Chrome 回報不支援 generateKeyFrame() 後會直接釋放此控制器。
    // 現行規範將 options 定義為 optional，但本專案目標 iOS 的 WebKit 實作仍要求
    // constructor 至少收到兩個參數；只傳 worker 會拋出 TypeError: Not enough arguments。
    // 傳入空物件只為相容該 WebKit 參數數量限制，不包含 MessagePort／MessageChannel。
    transform = new RTCRtpScriptTransform(worker, {})
  } catch (error) {
    clearTimeout(capabilityTimeoutId)
    removeWorkerListeners()
    worker.terminate()
    throw error
  }

  return {
    // selectKeyFrameRequestPath() 會等待這個 Promise，再決定 encoded-transform
    // 或 set-parameters 路徑。
    capability,
    attach: () => {
      // 只有 Worker 已確認 generateKeyFrame() 存在時才會執行；Chrome 不會進入此分支。
      // 設定後，這個 video sender 的 encoded frames 會經過 Worker 透明轉送。
      sender.transform = transform
      attached = true
    },
    request: () => new Promise((resolve, reject) => {
      const requestId = ++keyFrameRequestSequence
      const timeoutId = setTimeout(() => {
        // 逾時只讓本次 IDR Promise 失敗，不會卸載 Transform 或中斷既有視訊。
        pendingRequests.delete(requestId)
        reject(new Error('Encoded Transform key frame 請求逾時。'))
      }, KEY_FRAME_REQUEST_TIMEOUT_MS)

      pendingRequests.set(requestId, { resolve, reject, timeoutId })

      // Dedicated Worker 內建通道會把訊息送到 Worker 的 self.onmessage。
      worker.postMessage({
        type: 'generate-key-frame',
        requestId
      })
    }),
    dispose: () => {
      // dispose 只用於能力不支援、掛載失敗、通話結束或頁面卸載時的資源清理，
      // 不會在 generateKeyFrame() 請求失敗時呼叫。
      if (disposed) return

      disposed = true
      settleCapability(false, 'transform-disposed')

      for (const pendingRequest of pendingRequests.values()) {
        clearTimeout(pendingRequest.timeoutId)
        pendingRequest.reject(new Error('通話已結束，key frame 請求已取消。'))
      }

      pendingRequests.clear()
      removeWorkerListeners()

      if (attached) {
        try {
          // 通話結束或頁面卸載時，才把已掛載的 Transform 從 sender 移除。
          sender.transform = null
        } catch (error) {
          // SIP session 的結束流程可先關閉 PeerConnection；若 setter 拋錯，只完成本地資源清理。
        }
      }

      worker.terminate()
    }
  }
}

const createSetParametersKeyFramePath = (sender, selectionReason, selectionError = null) => ({
  // 這是 set-parameters 策略；正常預期由 Chromium 使用，Transform 初始化失敗時
  // 其他瀏覽器也會選到此 fallback，但實際 request() 仍可因 API 未實作而失敗。
  method: KEY_FRAME_REQUEST_METHOD.SET_PARAMETERS,
  selectionReason,
  selectionError,
  request: async () => {
    // 保留 sender 現有參數，透過 Chromium 已實作的 W3C 擴充參數要求各 encoding 產生 key frame。
    const senderParameters = sender.getParameters()
    const encodingCount = (senderParameters.encodings && senderParameters.encodings.length) || 0

    if (encodingCount === 0) {
      throw new Error('RTCRtpSender 沒有可用的 video encoding，無法要求產生 key frame。')
    }

    const encodingOptions = Array.from({ length: encodingCount }, () => ({ keyFrame: true }))
    await sender.setParameters(senderParameters, { encodingOptions })

    return { encodingCount }
  },
  dispose: () => {}
})

const createEncodedTransformKeyFramePath = (control, selectionReason) => ({
  // 這個物件是 Safari／Firefox 的策略；真正的 API 呼叫發生在 Worker。
  method: KEY_FRAME_REQUEST_METHOD.ENCODED_TRANSFORM,
  selectionReason,
  selectionError: null,
  request: async () => {
    await control.request()
    return {}
  },
  dispose: control.dispose
})

const selectKeyFrameRequestPath = async (sender, notifyWorkerFailure) => {
  // 缺少 Transform 基礎 API 時，不建立 Worker，直接選 set-parameters 策略。
  if (!hasEncodedTransformInfrastructure(sender)) {
    return createSetParametersKeyFramePath(sender, 'encoded-transform-infrastructure-unsupported')
  }

  let transformControl

  try {
    // 此時只建立「未掛載」的 Transform 做能力探測，不碰 sender 的視訊管線。
    transformControl = createEncodedTransformControl(sender, notifyWorkerFailure)
  } catch (error) {
    return createSetParametersKeyFramePath(sender, 'encoded-transform-initialization-failed', error)
  }

  const capability = await transformControl.capability

  if (!capability.supported) {
    // Transform 尚未掛載，所以能力不支援或診斷失敗時可安全釋放探測 Worker。
    // reason 與 error 交給呼叫端記錄，直接指出 fallback 原因。
    transformControl.dispose()
    return createSetParametersKeyFramePath(sender, capability.reason, capability.error)
  }

  try {
    // 只有確認 Worker 內有 generateKeyFrame() 後，才把 Transform 接到 sender。
    transformControl.attach()
  } catch (error) {
    transformControl.dispose()
    return createSetParametersKeyFramePath(sender, 'encoded-transform-attachment-failed', error)
  }

  return createEncodedTransformKeyFramePath(transformControl, capability.reason)
}

export const createKeyFrameRequestManager = ({ onFallback, onWorkerFailure } = {}) => {
  // key 是 RTCRtpSender，value 是「正在選擇／已選定 IDR 請求策略」的 Promise。
  const requestPathPromises = new Map()

  // key 是 RTCPeerConnection，value 是該通話的 video sender。
  // JsSIP 的 ended／failed 事件觸發前會先呼叫 RTCPeerConnection.close()（見 RTCSession.js
  // 的 _close()），close() 會停止所有 transceiver，使 getSenders() 在事件處理當下已回傳
  // 空陣列，無法用來反查 sender。因此改在 start() 當下（PeerConnection 仍開啟）記錄配對，
  // 供 disposePeerConnection() 之後查表。使用 WeakMap 是為了不延長已結束通話 PeerConnection
  // 的生命週期，通話物件在其他參照釋放後仍可被回收。
  const peerConnectionVideoSenders = new WeakMap()

  // 只有「本該走 encoded-transform 卻因故失敗」才通知呼叫端。
  // 因為選路結果會被快取，每個 sender 至多通知一次。
  const notifyFallbackIfAbnormal = (path) => {
    if (typeof onFallback !== 'function') return
    if (path.method !== KEY_FRAME_REQUEST_METHOD.SET_PARAMETERS) return
    if (!ABNORMAL_FALLBACK_REASONS.includes(path.selectionReason)) return

    onFallback({ selectionReason: path.selectionReason, error: path.selectionError })
  }

  // 與 onFallback 分開：onFallback 講的是「選路階段就退回 setParameters」，
  // 這裡講的是「已在運作的 encoded-transform 路徑中途壞掉」，後者代表 outbound 視訊
  // 永久停送且不自愈，嚴重度與處理方式都不同，因此讓上層能各自告警。
  const notifyWorkerFailure = (reason, error) => {
    if (typeof onWorkerFailure !== 'function') return

    try {
      onWorkerFailure({ reason, error })
    } catch (notifyError) {
      // 告警回呼失敗不得影響既有通話與 IDR 請求路徑，維持 fail-open。
    }
  }

  const preparePath = (sender) => {
    // 第一次呼叫會建立並快取 Promise；同一個 sender 再次呼叫只會取回同一個 Promise。
    // 因此同一通話的每 10 秒週期請求只會建立一個 Worker。
    const existingPathPromise = requestPathPromises.get(sender)

    if (existingPathPromise) return existingPathPromise

    // pathPromise 最後會 resolve 成包含 method、request()、dispose() 的內部策略物件。
    const pathPromise = selectKeyFrameRequestPath(sender, notifyWorkerFailure)
      .catch((error) => createSetParametersKeyFramePath(sender, 'path-selection-failed', error))
      .then((path) => {
        try {
          notifyFallbackIfAbnormal(path)
        } catch (notifyError) {
          // 告警回呼失敗不得影響 IDR 請求路徑，維持 fail-open。
        }
        return path
      })

    requestPathPromises.set(sender, pathPromise)
    return pathPromise
  }

  const request = async (sender) => {
    // selectedPath 是已選定的策略物件，不是網路路徑。
    // 該通話的第一次 request() 會在此完成選路並等待能力探測，後續請求直接取回快取。
    const selectedPath = await preparePath(sender)

    // Fail-open：產生 IDR 是附加控制操作，失敗不得改動既有 RTP 視訊資料路徑。
    // encoded-transform 一旦掛載便維持透明透傳；不因請求失敗或逾時而卸載、終止 Worker，
    // 也不在執行期改選另一個 IDR API，避免錯誤處理改動通話中的 sender。
    const details = await selectedPath.request()
    return {
      method: selectedPath.method,
      details,
      selectionReason: selectedPath.selectionReason,
      selectionError: selectedPath.selectionError
    }
  }

  // start() 時呼叫，記錄本通通話的 PeerConnection 與 video sender 配對；
  // 必須在 PeerConnection 仍開啟時呼叫，見上方 peerConnectionVideoSenders 的說明。
  const registerCall = (peerConnection, videoSender) => {
    if (!peerConnection || !videoSender) return
    peerConnectionVideoSenders.set(peerConnection, videoSender)
  }

  const disposePeerConnection = (peerConnection) => {
    if (!peerConnection) return

    // 通話 ended／failed 抵達時 PeerConnection 已被 JsSIP 關閉，getSenders() 為空，
    // 因此改查 start() 時記錄的配對，而非重新從 PeerConnection 取得 sender。
    const sender = peerConnectionVideoSenders.get(peerConnection)
    if (!sender) return

    peerConnectionVideoSenders.delete(peerConnection)

    const pathPromise = requestPathPromises.get(sender)
    requestPathPromises.delete(sender)
    if (pathPromise) pathPromise.then((path) => path.dispose())
  }

  const disposeAll = () => {
    // sipClient.destroy() 時呼叫，避免 Worker 留在背景執行。
    for (const [sender, pathPromise] of requestPathPromises) {
      requestPathPromises.delete(sender)
      pathPromise.then((path) => path.dispose())
    }
  }

  return {
    request,
    // 對外開放讓呼叫端能在通話開始時先行預熱選路，不必等到第一次 request()；
    // 回傳的就是同一個快取 Promise，因此預熱與後續請求共用同一個 Worker。
    preparePath,
    registerCall,
    disposePeerConnection,
    disposeAll
  }
}
